/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */

import React from 'react';
import { Navigate, Route, Routes as RoutesDom } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { styled } from '@mui/styles';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import ErrorsPage from '../pages/home/error/ErrorsPage';
import LogoutPage from '../pages/auth/Logout';
import { AuthPage } from '../pages/auth/AuthPage';
import { LayoutContextProvider } from '../../_base';
import Layout from '../../_base/layout/Layout';
import { IAppState } from '../store/rootDuck';
import { withRouter } from './withRouter';
import CompanyView from '../pages/home/companies/CompanyView';
import ProductView from '../pages/home/products/ProductView';
import CategoriesPage from '../pages/home/categories';
import UsersPage from '../pages/home/users';
import CompaniesPage from '../pages/home/companies';
import UserDocPage from '../pages/home/userDocs';
import SettingsPage from '../pages/home/settings';
import ProductsPage from '../pages/home/products';
import OrdersPage from '../pages/home/orders';
import StorePage from '../pages/home/store';
import CartPage from '../pages/home/cart';
import ReadDocPage from '../pages/home/userDocs/ReadDocPage';
import ProductNewCatalog from '../pages/home/products/ProductNewCatalog';

export type ProtectedRouteProps = {
  isAuthorized: boolean;
  outlet: JSX.Element;
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: '#6a96c3 !important',
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: '#bf1f2c !important',
  },
}));

export default function ProtectedRoute({ isAuthorized, outlet }: ProtectedRouteProps) {
  if (isAuthorized) {
    return outlet;
  }
  return <Navigate to='/error/error-v1' />;
}

export const Routes = withRouter(props => {
  const { isAuthorized, configMenu } = useSelector(
    ({ auth, profile, i18n, builder: { menuConfig } }: IAppState) => ({
      configMenu: menuConfig,
      isAuthorized: auth.user != null,
      me: profile.me,
      // @ts-ignore
      locale: i18n.lang,
    }),
    shallowEqual
  );

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
      }}
    >
      <LayoutContextProvider history={props} menuConfig={configMenu}>
        <RoutesDom>
          <Route path='auth/*' element={<AuthPage />} />
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout/*' element={<LogoutPage />} />
          {/* {isAuthorized ? ( */}
          <Route path='/' element={<Layout />}>
            <Route path='/' element={<ProductNewCatalog />} />
            <Route
              path='settings/*'
              element={
                <ProtectedRoute isAuthorized={isAuthorized} outlet={<SettingsPage />} />
              }
            />
            <Route path='categories/*' element={<CategoriesPage />} />
            <Route
              path='documents/*'
              element={<ProtectedRoute isAuthorized={isAuthorized} outlet={<UserDocPage />} />}
            />
            <Route path='document/read/:type' element={<ReadDocPage />} />
            <Route
              path='users/*'
              element={<ProtectedRoute isAuthorized={isAuthorized} outlet={<UsersPage />} />}
            />
            <Route
              path='companies/*'
              element={
                <ProtectedRoute isAuthorized={isAuthorized} outlet={<CompaniesPage />} />
              }
            />
            <Route
              path='orders/*'
              element={<ProtectedRoute isAuthorized={isAuthorized} outlet={<OrdersPage />} />}
            />
            <Route
              path='viewcompany/:id'
              element={<ProtectedRoute isAuthorized={isAuthorized} outlet={<CompanyView />} />}
            />
            <Route path='products/*' element={<ProductsPage />} />
            <Route path='cart/*' element={<CartPage />} />
            <Route path='viewproduct/:id' element={<ProductView />} />

            <Route
              path='stores/*'
              element={<ProtectedRoute isAuthorized={isAuthorized} outlet={<StorePage />} />}
            />

            <Route path='*' element={<Navigate to='/error/error-v1' />} />
          </Route>
          <Route path='*' element={<Navigate to='/error/error-v1' />} />
        </RoutesDom>
      </LayoutContextProvider>
    </SnackbarProvider>
  );
});
